import React, { FC, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { Editor, type IAllProps } from '@tinymce/tinymce-react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@/hooks/useDebounce';
import { WysiwygEditorProps } from '@/editor/PageEditor/EditorBlock/TextBlock/WysiwygEditor';
import { MaterialSymbol } from '@/components/MaterialSymbol';

type TinyMCEEditorInstance = Parameters<NonNullable<IAllProps['onInit']>>[1];

// Empty paragraphs left at the cut, e.g. when the cursor is at the end of a paragraph
const trimEmptyBlocks = (html: string) =>
  html
    .replace(/^(\s*<(p|h[1-6])>(\s|&nbsp;|<br\s*\/?>)*<\/\2>)+/i, '')
    .replace(/(<(p|h[1-6])>(\s|&nbsp;|<br\s*\/?>)*<\/\2>\s*)+$/i, '')
    .trim();

/** The editor's content split at the cursor. The DOM range splits a paragraph or list item the cursor is in. */
const splitAtCursor = (editor: TinyMCEEditorInstance): { before: string; after: string } | null => {
  const body = editor.getBody();
  const cursor = editor.selection.getRng();
  const range = editor.dom.createRng();
  range.setStart(cursor.startContainer, cursor.startOffset);
  range.setEnd(body, body.childNodes.length);
  const container = editor.dom.create('div');
  container.appendChild(range.cloneContents());
  const after = trimEmptyBlocks(editor.serializer.serialize(container, { getInner: true }));
  if (!after) return null;
  range.deleteContents();
  return { before: trimEmptyBlocks(editor.getContent()), after };
};

/** Where the cursor is, in the editor's iframe. An empty line has no text rect, so its element is used. */
const getCursorTop = (editor: TinyMCEEditorInstance): number | null => {
  const range = editor.selection.getRng();
  const rect = range.getClientRects()[0];
  if (rect && (rect.top || rect.height)) return rect.top;
  const node = range.startContainer;
  const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  return element ? element.getBoundingClientRect().top : null;
};

export const TinyEditor: FC<WysiwygEditorProps> = ({ value, onChange, toolbar, onSplit }) => {
  const { t } = useTranslation('components');
  const onSplitRef = useRef(onSplit);
  onSplitRef.current = onSplit;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const splitToggleRef = useRef<{ setActive: (active: boolean) => void } | null>(null);
  const [editorReady, setEditorReady] = useState<boolean>(false);
  // Splitting is two steps: split mode shows where the cut goes, and "Split here" does it
  const [splitMode, setSplitMode] = useState<boolean>(false);
  const [splitLineTop, setSplitLineTop] = useState<number | null>(null);
  const [nothingToSplit, setNothingToSplit] = useState<boolean>(false);
  const [editorValue, setEditorValue] = useState(value || '');
  useEffect(() => {
    setEditorValue(value || '');
  }, [value]);

  const editorRef = useRef<TinyMCEEditorInstance | null>(null);
  let cleaned_string: string;
  useDebounce(400, editorValue, onChange);

  // In split mode a line follows the cursor, and what will move is shaded
  useEffect(() => {
    const editor = editorRef.current;
    splitToggleRef.current?.setActive(splitMode);
    if (!editor || !editorReady) return;
    editor.getBody().classList.toggle('m24-split-mode', splitMode);
    if (!splitMode) {
      setSplitLineTop(null);
      return;
    }
    const update = () => {
      const iframe = editor.iframeElement;
      const cursorTop = getCursorTop(editor);
      if (!iframe || !wrapperRef.current || cursorTop === null) return;
      const iframeRect = iframe.getBoundingClientRect();
      const top = Math.min(Math.max(cursorTop, 0), iframeRect.height);
      setSplitLineTop(iframeRect.top - wrapperRef.current.getBoundingClientRect().top + top);
      setNothingToSplit(false);
    };
    update();
    const events = 'SelectionChange NodeChange keyup click input';
    editor.on(events, update);
    const win = editor.getWin();
    win.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      editor.off(events, update);
      win.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [splitMode, editorReady]);

  const handleSplit = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const parts = splitAtCursor(editor);
    if (!parts) {
      setNothingToSplit(true);
      return;
    }
    setSplitMode(false);
    setEditorValue(parts.before);
    onSplitRef.current?.(parts.before, parts.after);
  };

  const iframeBottom = (() => {
    const iframe = editorRef.current?.iframeElement;
    if (!iframe || !wrapperRef.current) return null;
    return iframe.getBoundingClientRect().bottom - wrapperRef.current.getBoundingClientRect().top;
  })();

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative', width: '100%' }}>
      <Editor
        tinymceScriptSrc='/tinymce/tinymce.min.js'
        onInit={(_evt, editor) => {
          editorRef.current = editor;
          setEditorReady(true);
        }}
        value={editorValue}
        onEditorChange={(newValue) => {
          setEditorValue(newValue);
        }}
        init={{
          entity_encoding: 'raw',
          width: '100%',
          menubar: false,
          contextmenu: false,
          plugins: ['autoresize', 'lists', 'link', 'anchor', 'code'],
          toolbar: `${toolbar ?? 'blocks | bold italic | bullist numlist | link | removeformat | code | paste'}${
            onSplit ? ' | m24split' : ''
          }`,
          setup: (editor) => {
            editor.ui.registry.addToggleButton('m24split', {
              icon: 'cut',
              text: t('TextBlock.SplitMode'),
              tooltip: t('TextBlock.SplitModeTooltip'),
              onSetup: (api) => {
                splitToggleRef.current = api;
                return () => {
                  splitToggleRef.current = null;
                };
              },
              onAction: () => setSplitMode((on) => !on),
            });
          },
          content_style:
            'body { font-family:Roboto,Helvetica,Arial,sans-serif; font-size:14px;min-height: 100px; line-height: 1.5; } ul { padding-left: 1.5em; list-style-type: disc; margin: 1.5em 0; } ul ul { padding-left:1.5em; margin: 0; } li { margin: 0.25em 0; } h3,h4,h5 { margin-top:2em;margin-bottom: -.75em; } h4 { font-style: italic; } h5 { font-size: .9em; text-transform: uppercase; } .mce-content-body { min-height: 100px; } .m24-split-mode { caret-color: #d32f2f; }',
          block_formats: 'Paragraph=p; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5;',
          max_height: 400,
          min_height: 300,
          extended_valid_elements: 'b',
          force_br_newlines: true,
          paste_preprocess: (_editor, e) => {
            cleaned_string = e.content;
            cleaned_string = cleaned_string.replace(/style=".*?"/gi, '');
            cleaned_string = cleaned_string.replace(/class=".*?"/gi, '');
            cleaned_string = cleaned_string.replace(/id=".*?"/gi, '');
            cleaned_string = cleaned_string.replace(/\sdata-[^"'\s]+(?:=["'][^"']*["'])?=["'][^"']*["']/gi, '');
            cleaned_string = cleaned_string.replace(/\saria-[^"'\s]+(?:=["'][^"']*["'])?=["'][^"']*["']/gi, '');
            cleaned_string = cleaned_string.replace(/\sng-[^"'\s]+(?:=["'][^"']*["'])?=["'][^"']*["']/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?span[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<button[^>]*>.*?<\/button>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?section[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?article[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?main[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?nav[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?footer[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?header[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?figure[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?figcaption[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?img[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?video[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?audio[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?div[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?fieldset[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?label[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?checkbox[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?radio[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?input[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?iframe[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?small[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<\/?smaller[^>]*>/gi, '');
            cleaned_string = cleaned_string.replace(/<h1>/gi, '<h2>');
            cleaned_string = cleaned_string.replace(/<\/h1>/gi, '</h2>');
            cleaned_string = cleaned_string.replace(/<(|\/)(html|body|meta)[^>]*?>/gi, '').trim();
            cleaned_string = cleaned_string.replace(/^\s*(<br\s*\/?\s*>)+|(<br\s*\/?\s*>)+\s*$/gi, '');
            cleaned_string = cleaned_string.replace(/<(\w+)\s+>/g, '<$1>');
            cleaned_string = cleaned_string.replace(/<li>&nbsp;<\/li>/gi, '');
            cleaned_string = cleaned_string.replace(/<li><\/li>/gi, '');
            cleaned_string = cleaned_string.replace(/<ul><\/ul>/gi, '');

            e.content = cleaned_string;
          },
          formats: {
            bold: { inline: 'b', remove: 'all' },
          },
          paste_block_drop: false,
          paste_merge_formats: true,
          paste_data_images: false,
        }}
      />
      {splitMode && splitLineTop !== null && (
        <>
          {/* What moves to the new module */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: splitLineTop,
              height: Math.max((iframeBottom ?? splitLineTop) - splitLineTop, 0),
              backgroundColor: 'rgba(211, 47, 47, 0.07)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: splitLineTop - 1,
              borderTop: '2px dashed #d32f2f',
              pointerEvents: 'none',
            }}>
            <MaterialSymbol
              name='content_cut'
              sx={{ position: 'absolute', left: -4, top: -13, fontSize: 20, color: '#d32f2f', background: 'white' }}
            />
          </Box>
        </>
      )}
      {splitMode && (
        <Alert
          severity={nothingToSplit ? 'warning' : 'info'}
          variant='outlined'
          sx={{ mt: 1 }}
          action={
            <>
              <Button color='inherit' size='small' onClick={() => setSplitMode(false)}>
                {t('TextBlock.SplitCancel')}
              </Button>
              <Button color='error' variant='contained' size='small' onClick={handleSplit}>
                {t('TextBlock.SplitHere')}
              </Button>
            </>
          }>
          {nothingToSplit ? t('TextBlock.SplitNothingAfter') : t('TextBlock.SplitModeInfo')}
        </Alert>
      )}
    </Box>
  );
};
